import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Header } from '@app/navigation/header';
import { AI_TOOLS, INPUT_KEYS } from '@constants';
import { ROUTES } from '@constants/routes';
import { GenericQuery } from '@features/query/GenericQuery';
import { DOCUMENT_SELECTION_MODES } from '@features/document/DocumentSelectionModal/modes';
import { scrollToTop } from '@utils/usefulFuncs';

import { Body, Body5, Container, Page } from '@ui';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { AdvancedSettingsButton } from './AdvancedSettings';
import { AnswerConfigContext } from '@/contexts/AnswerConfigContext';
import { AdvancedSettingsProvider } from '@/contexts/AdvancedSettingsContext';
import { ModeContext } from '@/contexts/ModeContext';
import { QueryControllerProvider } from '@/contexts/QueryControllerContext';

const tool = AI_TOOLS.QUERY_AGENT_SCRIPT;

const answerConfig = {
  enableMoreDetail: false,
  enableLinks: true,
  ignoreLatex: false,
  enableRating: false,
  enableShare: false,
};

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const mapSubmitPayload = (apiFormData) => {
  const payload = {
    ...apiFormData,
    user_mode: tool,
    include_executed_plan_data: true,
  };
  delete payload.displayQuestion;
  return payload;
};

const buildModeContext = (setPageError) => ({
  tool,
  autoSelectDefaultDocuments: false,
  autoSelectRecentDocuments: false,
  documentSelectionModes: [DOCUMENT_SELECTION_MODES.allCompany, DOCUMENT_SELECTION_MODES.myDocs],
  defaultActiveDocumentSelectionMode: DOCUMENT_SELECTION_MODES.allCompany,
  showCompanyTags: true,
  inputs: {
    [INPUT_KEYS.companiesTags]: true,
    [INPUT_KEYS.agent]: true,
    [INPUT_KEYS.question]: true,
    [INPUT_KEYS.sources]: true,
  },
  validateSubmit: (formData) => {
    if (!formData.agentName) {
      return 'Please select an agent';
    }
    return null;
  },
  mapSubmitPayload,
  onAuthError: setPageError,
});

function AgentScriptQueryPage() {
  const navigate = useNavigate();
  const [pageError, setPageError] = React.useState(null);
  const modeContext = React.useMemo(() => buildModeContext(setPageError), [setPageError]);

  React.useEffect(() => {
    scrollToTop();
  }, []);

  const onGoBack = () => navigate(ROUTES.home);

  if (pageError) {
    return (
      <PageWrapper>
        <Header />
        <Body withFooter>
          <Page width={'1200px'}>
            <BackArrowButton onClick={onGoBack} />
            <Container top={24} bottom={400}>
              <Body5>{pageError}</Body5>
            </Container>
          </Page>
        </Body>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <Page width={'1200px'}>
          <BackArrowButton onClick={onGoBack} />
          <AdvancedSettingsButton tool={tool} />
          <Container top={44}>
            <ModeContext.Provider value={modeContext}>
              <AdvancedSettingsProvider>
                <QueryControllerProvider>
                  <AnswerConfigContext.Provider value={answerConfig}>
                    <GenericQuery resultAnchorId="question-answer" />
                  </AnswerConfigContext.Provider>
                </QueryControllerProvider>
              </AdvancedSettingsProvider>
            </ModeContext.Provider>
          </Container>
        </Page>
      </Body>
    </PageWrapper>
  );
}

export default AgentScriptQueryPage;
